import React, { useState, useEffect, useCallback } from 'react';
import { tagsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { TagChip } from './TagMultiSelect';
import { Plus, Pencil, Trash2, Merge, Loader2, Tag, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

function TagRow({ tag, allTags, onRename, onDelete, onMerge }) {
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(tag.name);
  const [merging, setMerging] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');

  const handleRename = async () => {
    if (!newName.trim() || newName.trim() === tag.name) { setEditing(false); return; }
    await onRename(tag.id, newName.trim());
    setEditing(false);
  };

  const handleMerge = async () => {
    if (!mergeTarget) return;
    await onMerge(tag.id, mergeTarget);
    setMerging(false);
    setMergeTarget('');
  };

  const mergeOptions = allTags.filter(t => t.id !== tag.id && t.entity_type === tag.entity_type);

  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors" data-testid={`tag-row-${tag.id}`}>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(false); }}
              data-testid="tag-rename-input"
            />
            <Button size="sm" variant="ghost" onClick={handleRename} data-testid="tag-rename-save">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : merging ? (
          <div className="flex items-center gap-2">
            <span className="text-sm">Merge into:</span>
            <Select value={mergeTarget} onValueChange={setMergeTarget}>
              <SelectTrigger className="h-8 w-[180px]" data-testid="tag-merge-select"><SelectValue placeholder="Select tag..." /></SelectTrigger>
              <SelectContent>
                {mergeOptions.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="default" onClick={handleMerge} disabled={!mergeTarget} data-testid="tag-merge-confirm">Merge</Button>
            <Button size="sm" variant="ghost" onClick={() => { setMerging(false); setMergeTarget(''); }}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <TagChip name={tag.name} />
            <span className="text-xs text-muted-foreground">{tag.usage_count} {tag.usage_count === 1 ? 'use' : 'uses'}</span>
          </div>
        )}
      </div>
      {!editing && !merging && (
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setNewName(tag.name); setEditing(true); }} data-testid={`tag-edit-${tag.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          {mergeOptions.length > 0 && (
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setMerging(true)} data-testid={`tag-merge-${tag.id}`}>
              <Merge className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(tag.id)} data-testid={`tag-delete-${tag.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function TagList({ entityType, label, icon: Icon }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tagsAPI.list({ entity_type: entityType });
      setTags(res.data || []);
    } catch { toast.error('Failed to load tags'); }
    setLoading(false);
  }, [entityType]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    setCreating(true);
    try {
      await tagsAPI.create({ name: newTagName.trim(), entity_type: entityType });
      toast.success(`Tag "${newTagName.trim()}" created`);
      setNewTagName('');
      fetchTags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create tag');
    }
    setCreating(false);
  };

  const handleRename = async (id, name) => {
    try {
      await tagsAPI.update(id, { name });
      toast.success('Tag renamed');
      fetchTags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to rename tag');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tag? It will be removed from all linked records.')) return;
    try {
      await tagsAPI.remove(id);
      toast.success('Tag deleted');
      fetchTags();
    } catch { toast.error('Failed to delete tag'); }
  };

  const handleMerge = async (sourceId, targetId) => {
    try {
      await tagsAPI.merge({ source_tag_id: sourceId, target_tag_id: targetId });
      toast.success('Tags merged');
      fetchTags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to merge tags');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          placeholder={`New ${label.toLowerCase()} tag...`}
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          className="max-w-xs"
          data-testid={`new-${entityType}-tag-input`}
        />
        <Button size="sm" onClick={handleCreate} disabled={creating || !newTagName.trim()} data-testid={`create-${entityType}-tag-btn`}>
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Add
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : tags.length === 0 ? (
        <div className="text-center py-8">
          <Icon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No {label.toLowerCase()} tags yet. Create one above.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {tags.map(t => (
            <TagRow key={t.id} tag={t} allTags={tags} onRename={handleRename} onDelete={handleDelete} onMerge={handleMerge} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TagManagement() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-primary" />
          Tag Management
        </CardTitle>
        <CardDescription>
          Create, rename, merge, and delete tags for customers and jobs. Tags are shared across your organization.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="customer" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="customer" className="gap-2" data-testid="tags-tab-customer">
              <Users className="w-4 h-4" />
              Customer Tags
            </TabsTrigger>
            <TabsTrigger value="job" className="gap-2" data-testid="tags-tab-job">
              <Briefcase className="w-4 h-4" />
              Job Tags
            </TabsTrigger>
          </TabsList>
          <TabsContent value="customer">
            <TagList entityType="customer" label="Customer" icon={Users} />
          </TabsContent>
          <TabsContent value="job">
            <TagList entityType="job" label="Job" icon={Briefcase} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
